/**
 * Canonical normalization of a free-text Shopify `productType` into the
 * policy/engine fuel-type **token** vocabulary (`gels`, `high_carb_drink`, …).
 *
 * This is the single source of truth for "what fuel type is this product",
 * shared by:
 *   - catalog sync (`shopify/productSync.ts`) — writes the normalized token to
 *     `ShopifyProduct.productTypeToken` at upsert time so it's a real, indexable
 *     DB attribute;
 *   - the swap-candidate query + scorer (`fuelPlans/*`) — match/score on the
 *     same token.
 *
 * It lives in `src/lib` (depending only on `preferenceOptions`) specifically to
 * avoid a `shopify → fuelPlans → shopify` import cycle: previously the function
 * lived in `fuelPlans/localAlternatives.ts`, which `productSync` cannot import
 * because `fuelPlans` already imports `shopify`.
 *
 * Before this consolidation, the candidate query matched `productType` against
 * preference-UI **display labels** ("Stroopwaffles", "High carb drink") while
 * the scorer normalized to **tokens** — two vocabularies for one field, so a
 * product typed `"high_carb_drink"` or `"Stroopwafels"` resolved in the scorer
 * but was excluded by the query. Keying both off this one function (and the
 * persisted column it feeds) closes that divergence.
 */

import { FUEL_TYPES } from "./preferenceOptions.js";

const FUEL_TYPE_KEYS = new Set(FUEL_TYPES.map((o) => o.key));
const FUEL_TYPE_LABEL_TO_KEY: Record<string, string> = Object.fromEntries(
  FUEL_TYPES.map((o) => [o.label.toLowerCase(), o.key])
);

/**
 * Known merchant `productType` strings that don't match a token or label but
 * map cleanly to one. Mirrors the brand `VENDOR_ALIAS` pattern — a small,
 * explicit table, not fuzzy matching. The merchant types healthy snacks as
 * plain "Snacks"; without this they'd resolve to null and never appear as
 * Recovery swap candidates (`healthy_snacks` is in the Recovery family).
 * Keyed by lowercased input.
 */
const PRODUCT_TYPE_ALIAS: Record<string, string> = {
  snacks: "healthy_snacks"
};

/**
 * Normalize a free-text Shopify `productType` (e.g. "High carb drink",
 * "Gels", "Stroopwaffles") to the policy/engine token vocabulary
 * (e.g. "high_carb_drink", "gels", "stroopwafels"). Returns null when the
 * type can't be resolved — callers drop the candidate rather than guess.
 *
 * Resolution order:
 *   1. Already a known token → use as-is.
 *   2. Lowercased token → use that.
 *   3. Lowercase + spaces→underscores hits a known token → use that.
 *   4. Case-insensitive match against `FUEL_TYPES[*].label` → use the key.
 */
export function normalizeProductTypeToToken(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  if (FUEL_TYPE_KEYS.has(trimmed)) return trimmed;
  const lowered = trimmed.toLowerCase();
  if (FUEL_TYPE_KEYS.has(lowered)) return lowered;
  const underscored = lowered.replace(/\s+/g, "_");
  if (FUEL_TYPE_KEYS.has(underscored)) return underscored;
  const labelMatch = FUEL_TYPE_LABEL_TO_KEY[lowered];
  if (labelMatch) return labelMatch;
  const alias = PRODUCT_TYPE_ALIAS[lowered];
  if (alias) return alias;
  return null;
}
